import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  InputAdornment,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  useMediaQuery,
  Badge,
  Drawer,
  Skeleton,
  IconButton,
  AppBar,
  Toolbar,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Mail as MailIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  LockOpen as LockOpenIcon,
  PersonAdd as PersonAddIcon,
  Share as ShareIcon,
  ChevronLeft,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useAuth } from '../context/AuthProvider.tsx';
import { formatDistanceToNow } from 'date-fns';
import { useSwipeable } from 'react-swipeable';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#7b1fa2',
      light: '#9c27b0',
      dark: '#6a1b9a',
    },
    secondary: {
      main: '#29b6f6',
      light: '#4fc3f7',
      dark: '#0288d1',
    },
    background: {
      default: '#0A0F15',
      paper: '#1E2A35',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#94ADC7',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
    },
    success: {
      main: '#4CAF50',
      light: '#81c784',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600, letterSpacing: '0.5px' },
    h6: { fontWeight: 600, letterSpacing: '0.3px' },
    subtitle1: { color: '#94ADC7' },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    body1: {
      letterSpacing: '0.1px',
    },
    body2: {
      letterSpacing: '0.1px',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: 'none',
          padding: '8px 16px',
        },
        contained: {
          '&:hover': {
            boxShadow: '0 8px 16px 0 rgba(0,0,0,0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
          },
        },
      },
    },
  },
});

interface User {
  username: string;
  name: string;
  picture: string;
}

interface Friend extends User {
  acceptedTimestamp: string;
}

interface Notification {
  notificationID: string;
  message: string;
  timestamp: number;
  isRead: boolean;
  expiryTime: number;
  type: 'capsule' | 'friend' | 'capsule_access';
}

const NotificationItem: React.FC<{
  notification: Notification;
  onMarkAsRead: (notificationId: string) => void;
  shouldMarkRead: boolean;
}> = ({ notification, onMarkAsRead, shouldMarkRead }) => {
  const itemRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    if (shouldMarkRead && !notification.isRead) {
      onMarkAsRead(notification.notificationID);
    }
  }, [shouldMarkRead, notification.isRead, notification.notificationID, onMarkAsRead]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'capsule':
        return <LockOpenIcon sx={{
          color: notification.isRead
            ? 'text.secondary'
            : 'white',
          transition: 'color 0.2s ease-in-out',
        }} />;
      case 'friend':
        return <PersonAddIcon sx={{
          color: notification.isRead
            ? 'text.secondary'
            : 'white',
          transition: 'color 0.2s ease-in-out',
        }} />;
      case 'capsule_access':
        return <ShareIcon sx={{
          color: notification.isRead
            ? 'text.secondary'
            : 'white',
          transition: 'color 0.2s ease-in-out',
        }} />;
      default:
        return <NotificationsIcon sx={{
          color: notification.isRead
            ? 'text.secondary'
            : 'white',
          transition: 'color 0.2s ease-in-out',
        }} />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <ListItem
        ref={itemRef}
        component="li"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: notification.isRead ? 'transparent' : 'rgba(123, 31, 162, 0.08)',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.05)',
            transition: 'all 0.2s ease-in-out',
          },
          transition: 'all 0.2s ease-in-out',
          py: 2,
          mx: 1,
          borderRadius: 2,
          my: 0.5,
        }}
      >
        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor: notification.isRead
                ? 'rgba(255,255,255,0.1)'
                : 'primary.main',
              width: 40,
              height: 40,
              transition: 'all 0.2s ease-in-out',
              boxShadow: notification.isRead
                ? 'none'
                : '0 0 0 2px rgba(123, 31, 162, 0.2)',
            }}
          >
            {getNotificationIcon(notification.type)}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  fontWeight: notification.isRead ? 400 : 500,
                  lineHeight: 1.4,
                }}
              >
                {notification.message}
              </Typography>
              {!notification.isRead && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: '0 0 0 2px rgba(123, 31, 162, 0.2)',
                  }}
                />
              )}
            </Box>
          }
          secondary={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 0.5,
                color: 'text.secondary',
              }}
            >
              <Typography variant="caption">
                {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
              </Typography>
            </Box>
          }
        />
      </ListItem>
    </motion.div>
  );
};

const NotificationsPanel: React.FC<{
  open: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkAsRead: (notificationId: string) => void;
  isLoading: boolean;
}> = ({ open, onClose, notifications, onMarkAsRead, isLoading }) => {
  const [shouldMarkRead, setShouldMarkRead] = useState(false);
  const unreadNotifications = useRef<string[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Track unread notifications when panel opens
  useEffect(() => {
    if (open) {
      unreadNotifications.current = notifications
        .filter(n => !n.isRead)
        .map(n => n.notificationID);
    }
  }, [open, notifications]);

  const handleClose = () => {
    // Mark all tracked unread notifications as read when closing
    unreadNotifications.current.forEach(id => {
      onMarkAsRead(id);
    });
    unreadNotifications.current = [];
    onClose();
  };

  // Swipe gesture handler
  const swipeHandlers = useSwipeable({
    onSwipedRight: () => {
      handleClose();
    },
    trackMouse: true
  });

  // Get notification type color
  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case 'capsule':
        return '#4CAF50';
      case 'friend':
        return '#2196F3';
      case 'capsule_access':
        return '#E31B88';
      default:
        return '#7b1fa2';
    }
  };

  // Get notification type label
  const getNotificationTypeLabel = (type: string) => {
    switch (type) {
      case 'capsule':
        return 'Capsule Unlock';
      case 'friend':
        return 'Friend Request';
      case 'capsule_access':
        return 'Shared Capsule';
      default:
        return 'Notification';
    }
  };

  // Sort notifications by timestamp in descending order (newest first)
  const sortedNotifications = [...notifications].sort((a, b) => b.timestamp - a.timestamp);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500 },
          bgcolor: '#0A0F15',
          borderLeft: '1px solid',
          borderColor: 'divider',
          borderRadius: { xs: 0, sm: '16px 0 0 16px' },
          overflow: 'hidden',
          '& .MuiDrawer-paper': {
            bgcolor: '#0A0F15',
          },
        },
      }}
    >
      <Box {...swipeHandlers} sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0A0F15',
      }}>
        <AppBar
          position="static"
          color="transparent"
          elevation={0}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Toolbar sx={{ gap: 0, pl: 0 }}>
            <IconButton
              onClick={handleClose}
              sx={{
                color: '#fff',
                '&:hover': {
                  color: '#fff',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <ChevronLeft />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                flexGrow: 1,
                background: '#FFF',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 600,
              }}
            >
              Notifications
            </Typography>
            {notifications.filter(n => !n.isRead).length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {notifications.filter(n => !n.isRead).length} {notifications.filter(n => !n.isRead).length === 1 ? 'notification' : 'notifications'}
              </Typography>
            )}
          </Toolbar>
        </AppBar>

        <Box sx={{
          flex: 1,
          overflow: 'auto',
          px: 2,
          py: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '4px',
          },
        }}>
          {isLoading ? (
            Array(5).fill(0).map((_, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton
                    variant="text"
                    width="80%"
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                  />
                  <Skeleton
                    variant="text"
                    width="40%"
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                  />
                </Box>
              </Box>
            ))
          ) : sortedNotifications.length === 0 ? (
            <Box sx={{
              p: 4,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              color: 'text.secondary',
              mt: 4,
            }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'rgba(123, 31, 162, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <NotificationsIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.8 }} />
              </Box>
              <Typography variant="h6" sx={{
                color: 'text.primary',
                fontWeight: 500,
                background: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                All Caught Up!
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, maxWidth: 280, mx: 'auto' }}>
                You're up to date with all your notifications. We'll notify you when something new happens.
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {sortedNotifications.map((notification) => (
                <Box key={notification.notificationID} sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 3,
                      height: '70%',
                      borderRadius: 4,
                      bgcolor: getNotificationTypeColor(notification.type),
                      opacity: notification.isRead ? 0.3 : 1,
                    }}
                  />
                  <NotificationItem
                    notification={notification}
                    onMarkAsRead={onMarkAsRead}
                    shouldMarkRead={shouldMarkRead}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      left: 80,
                      top: 0,
                      color: getNotificationTypeColor(notification.type),
                      opacity: notification.isRead ? 0.5 : 1,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: 500,
                    }}
                  >
                    {getNotificationTypeLabel(notification.type)}
                  </Typography>
                </Box>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

const FriendsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'discover' | 'friends' | 'requests'>('discover');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<Friend[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Add swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (activeSection === 'discover') setActiveSection('friends');
      else if (activeSection === 'friends') setActiveSection('requests');
    },
    onSwipedRight: () => {
      if (activeSection === 'requests') setActiveSection('friends');
      else if (activeSection === 'friends') setActiveSection('discover');
    },
    trackMouse: true,
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleUserClick = (username: string) => {
    navigate(`/profile/${username}`);
  };

  const formatFriendsSince = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const fetchFriends = useCallback(async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const authToken = tokens?.accessToken?.toString();
      const userId = user?.username;
      const response = await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/friends/accepted/${userId}?status=accepted`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': authToken!,
        },
      });
      const data = await response.json();
      setFriends(data);
    } catch (error) {
      console.error('Error fetching friends:', error);
      setFriends([]);
    }
  }, [user]);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const authToken = tokens?.accessToken?.toString();
        const userId = user?.username;
        const response = await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/friends/accepted/${userId}?status=incoming`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': authToken!,
          },
        });
        const data = await response.json();
        setRequests(data);
      } catch (error) {
        console.error('Error fetching friend requests:', error);
        setRequests([]);
      }
    };

    fetchRequests();
    fetchFriends();
  }, [fetchFriends, user]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (searchQuery.trim()) {
        try {
          const { tokens } = await fetchAuthSession();
          const authToken = tokens?.accessToken?.toString();
          const response = await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/users?query=${searchQuery}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': authToken!
            }
          });
          const data = await response.json();
          setSearchResults(data.users);
        } catch (error) {
          console.error('Error fetching users:', error);
          setSearchResults([]);
        }
      } else {
        setSearchResults([]);
      }
    };
    fetchUsers();
  }, [searchQuery]);

  const handleFriendRequest = async (username: string, action: 'accept' | 'reject') => {
    try {
      const updatedRequests = requests.filter((request) => request.username !== username);
      setRequests(updatedRequests);

      const { tokens } = await fetchAuthSession();
      const authToken = tokens?.accessToken?.toString();
      const userId = user?.username;
      const response = await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/friend/request/${userId}/${username}?action=${action}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': authToken!,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to process friend request');
      }
      if (action === 'accept') {
        await fetchFriends(); // Immediately update friends list after accepting
      }
    } catch (error) {
      console.error(`Error handling friend request (${action}):`, error);
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoadingNotifications(true);
      const { tokens } = await fetchAuthSession();
      const authToken = tokens?.accessToken?.toString();
      const userId = user?.username;
      const response = await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': authToken!,
        },
      });
      const data = await response.json();
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    // Set up polling for new notifications every 60 seconds instead of 30
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setIsNotificationsOpen(true);
  };

  const handleNotificationClose = () => {
    setIsNotificationsOpen(false);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(notification =>
        notification.notificationID === notificationId
          ? { ...notification, isRead: true }
          : notification
      )
    );

    try {
      const { tokens } = await fetchAuthSession();
      const authToken = tokens?.accessToken?.toString();
      const userId = user?.username;
      await fetch(`https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': authToken!,
        },
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Revert optimistic update on error
      setNotifications(prev =>
        prev.map(notification =>
          notification.notificationID === notificationId
            ? { ...notification, isRead: false }
            : notification
        )
      );
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: 'radial-gradient(circle at 10% 10%, rgba(123, 31, 162, 0.1), transparent 30%), radial-gradient(circle at 90% 90%, rgba(29, 182, 246, 0.08), transparent 40%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          backgroundImage: 'linear-gradient(180deg, rgba(10, 15, 21, 0.9) 0%, rgba(10, 15, 21, 1) 100%)',
          zIndex: 0,
        }
      }}>
        <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: 2, zIndex: 2 }}>
          {isMobile &&
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
              <Typography variant="h5"
                component="h1"
                sx={{
                  width: "100%",
                  textAlign: "center",
                  fontSize: "1.5rem",
                  fontWeight: 400,
                  color: "white",
                  mb: { xs: 0, sm: 0 },
                }}>
                Friends & Connections
              </Typography>
            </Box>
          }

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            {['discover', 'friends', 'requests'].map((section) => (
              <Button
                key={section}
                onClick={() => setActiveSection(section as 'discover' | 'friends' | 'requests')}
                sx={{
                  mx: 1,
                  color: activeSection === section ? 'transparent' : 'text.primary',
                  borderBottom: 0,
                  borderRadius: 0,
                  position: 'relative',
                  backgroundImage: activeSection === section
                    ? 'linear-gradient(45deg, #7B2CBF, #E31B88)'
                    : 'none',
                  backgroundClip: activeSection === section ? 'text' : 'initial',
                  WebkitBackgroundClip: activeSection === section ? 'text' : 'initial',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: activeSection === section ? 'transparent' : 'primary.light',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: activeSection === section ? 2 : 0,
                    backgroundImage: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                  }
                }}
              >
                {section === 'requests' ? (
                  <Badge
                    badgeContent={requests.length}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -12,
                        top: 5,
                        border: `2px solid ${theme.palette.background.paper}`,
                        padding: '0px',
                      },
                      ...(activeSection === section && {
                        backgroundImage: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }),
                    }}
                  >
                    Requests
                  </Badge>
                ) : (
                  section.charAt(0).toUpperCase() + section.slice(1)
                )}
              </Button>
            ))}
          </Box>

          <Box {...swipeHandlers} sx={{ flex: 1, overflow: 'hidden' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                {activeSection === 'discover' && (
                  <>
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 2 }}>
                          Discover Users
                        </Typography>
                        <Badge
                          badgeContent={unreadCount}
                          color="error"
                          sx={{
                            '& .MuiBadge-badge': {
                              right: 10,
                              top: 13,
                              border: `2px solid ${theme.palette.background.paper}`,
                              padding: '0 4px',
                            },
                          }}
                        >
                          <IconButton
                            onClick={handleNotificationClick}
                            sx={{
                              position: 'relative',
                              width: 40,
                              height: 40,
                              backgroundColor: 'rgba(123, 31, 162, 0.08)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                backgroundColor: 'rgba(123, 31, 162, 0.15)'
                              },
                              transition: 'all 0.3s ease',
                              border: '1px solid rgba(123, 31, 162, 0.2)',
                              boxShadow: isMobile ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.15)'
                            }}
                          >
                            <NotificationsIcon
                              sx={{
                                fontSize: 22,
                                background: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                              }}
                            />
                          </IconButton>
                        </Badge>
                      </Box>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search users..."
                        size="small"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        sx={{
                          ml: "1px",
                          width: 'calc(100% - 2px)',
                          zIndex: 2,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            backdropFilter: 'blur(8px)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.08)',
                            },
                            '&.Mui-focused': {
                              '& fieldset': {
                                borderWidth: '2px',
                                borderColor: 'transparent',
                                borderRadius: '12px',
                              },
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: -1,
                                right: -1,
                                bottom: -1,
                                left: -1,
                                padding: '1px',
                                borderRadius: '12px',
                                background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
                                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                WebkitMaskComposite: 'xor',
                                maskComposite: 'exclude',
                                pointerEvents: 'none',
                              },
                            },
                            '& fieldset': {
                              borderColor: 'rgba(255,255,255,0.1)',
                              transition: 'border-color 0.3s ease',
                            },
                            padding: '2px 4px',
                            borderRadius: '12px',
                          },
                          '& .MuiInputBase-input': {
                            color: 'text.primary',
                            padding: '10px 14px',
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{
                                  ml: 2,
                                  fontSize: 20,
                                  background: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent'
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {searchResults.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <List>
                          {searchResults.map((user) => (
                            <ListItem
                              key={user.username}
                              onClick={() => handleUserClick(user.username)}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'rgba(255,255,255,0.08)'
                                },
                                borderRadius: 3,
                                transition: 'all 0.3s ease',
                                mb: 1.5,
                                p: 1.5,
                                border: '1px solid rgba(255,255,255,0.05)',
                                backgroundColor: 'rgba(30, 42, 53, 0.5)',
                                backdropFilter: 'blur(8px)',
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  src={user.picture}
                                  alt={user.username}
                                  sx={{
                                    width: 50,
                                    height: 50,
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    border: '2px solid rgba(123, 31, 162, 0.3)'
                                  }}
                                />
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      color: 'text.primary',
                                      fontWeight: 600
                                    }}
                                  >
                                    {user.name}
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5
                                    }}
                                  >
                                    @{user.username}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    <Divider sx={{ mb: 4 }} />
                  </>
                )}

                {activeSection === 'friends' && (
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {friends.length === 0 &&
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          p: 4,
                          mt: 8,
                          mb: 4,
                          mx: 'auto',
                          maxWidth: '400px',
                          borderRadius: 3,
                          backgroundColor: 'rgba(30, 42, 53, 0.4)',
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                          backdropFilter: 'blur(10px)'
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 70,
                            height: 70,
                            bgcolor: 'rgba(123, 31, 162, 0.15)',
                            mb: 2,
                            '& .MuiSvgIcon-root': {
                              fontSize: 35,
                            }
                          }}
                        >
                          <PersonAddIcon
                            sx={{
                              background: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              fontSize: 30
                            }}
                          />
                        </Avatar>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1,
                            background: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 600
                          }}
                        >
                          No Friends Yet
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 280, mb: 1 }}>
                          Search for users and send friend requests to connect with others on TimeCapsule
                        </Typography>
                      </Box>
                    }
                    <Grid container spacing={2}>
                      {friends.map((friend) => (
                        <Grid item xs={6} sm={6} md={4} lg={4} key={friend.username}>
                          <Card
                            sx={{
                              height: '100%',
                              backgroundColor: 'rgba(30, 42, 53, 0.5)',
                              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.05)',
                              backdropFilter: 'blur(8px)',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <CardActionArea
                              onClick={() => handleUserClick(friend.username)}
                              sx={{
                                borderRadius: 3,
                                height: '100%',
                                transition: 'all 0.3s ease',
                              }}
                            >
                              <CardContent sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                p: 2.5,
                                height: '100%'
                              }}>
                                <Avatar
                                  src={friend.picture}
                                  alt={friend.name}
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    mr: isMobile ? 0 : 2.5,
                                    mb: isMobile ? 1.5 : 0,
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    border: '2px solid rgba(123, 31, 162, 0.3)'
                                  }}
                                />
                                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      color: 'text.primary',
                                      fontWeight: 600,
                                      mb: 0.5
                                    }}
                                  >
                                    {friend.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      mb: 1
                                    }}
                                  >
                                    @{friend.username}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'inline-block',
                                      mt: 0.5,
                                      fontSize: '0.7rem',
                                      opacity: 0.8,
                                      padding: '0px 0px',
                                      borderRadius: '4px',
                                      color: 'rgba(255, 255, 255, 0.7)'
                                    }}
                                  >
                                    Friends since {formatFriendsSince(friend.acceptedTimestamp)}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {activeSection === 'requests' && (
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {requests.length === 0 &&
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          p: 4,
                          mt: 8,
                          mx: 'auto',
                          maxWidth: '400px',
                          borderRadius: 3,
                          backgroundColor: 'rgba(30, 42, 53, 0.4)',
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                          backdropFilter: 'blur(10px)'
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 70,
                            height: 70,
                            bgcolor: 'rgba(123, 31, 162, 0.15)',
                            mb: 2,
                            '& .MuiSvgIcon-root': {
                              fontSize: 35,
                            }
                          }}
                        >
                          <CheckCircleIcon
                            sx={{
                              background: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              fontSize: 30
                            }}
                          />
                        </Avatar>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1,
                            background: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 600
                          }}
                        >
                          No Friend Requests
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 280, mb: 1 }}>
                          You don't have any pending friend requests at the moment
                        </Typography>
                      </Box>
                    }
                    <List sx={{ px: 1 }}>
                      {requests.map((request) => (
                        <motion.div
                          key={request.username}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          style={{ overflow: 'hidden' }}
                        >
                          <ListItem
                            sx={{
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                              borderRadius: 2,
                              mb: 2,
                              transition: 'background-color 0.3s',
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar src={request.picture} alt={request.name} onClick={() => handleUserClick(request.username)} sx={{ width: 50, height: 50, cursor: 'pointer' }} />
                            </ListItemAvatar>
                            <ListItemText
                              primary={<Typography variant="subtitle1" sx={{ color: 'text.primary' }}>{request.name}</Typography>}
                              secondary={
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Sent you a friend request
                                </Typography>
                              }
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                sx={{
                                  color: 'white',
                                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                                  '&:hover': {
                                    bgcolor: 'rgba(76, 175, 80, 0.2)',
                                  },
                                  transition: 'all 0.3s ease',
                                  padding: '8px'
                                }}
                                onClick={() => handleFriendRequest(request.username, 'accept')}
                              >
                                <CheckIcon />
                              </IconButton>
                              <IconButton
                                sx={{
                                  color: 'white',
                                  bgcolor: 'rgba(244, 67, 54, 0.1)',
                                  '&:hover': {
                                    bgcolor: 'rgba(244, 67, 54, 0.2)',
                                  },
                                  transition: 'all 0.3s ease',
                                  padding: '8px'
                                }}
                                onClick={() => handleFriendRequest(request.username, 'reject')}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          </ListItem>
                        </motion.div>
                      ))}
                    </List>
                  </Box>
                )}
              </motion.div>
            </AnimatePresence>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Invite Friends
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              alignItems: 'stretch'
            }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter email address"
                size="small"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={isInviting}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(8px)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                    },
                    '&.Mui-focused': {
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: 'transparent',
                        borderRadius: '12px',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -1,
                        right: -1,
                        bottom: -1,
                        left: -1,
                        padding: '1px',
                        borderRadius: '12px',
                        background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        pointerEvents: 'none',
                      },
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                    padding: '10px 14px',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailIcon
                        sx={{
                          ml: 2,
                          fontSize: 18,
                          background: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={isInviting ? <CircularProgress size={20} color="inherit" /> : <MailIcon />}
                disabled={isInviting || !inviteEmail}
                onClick={async () => {
                  try {
                    setIsInviting(true);
                    const { tokens } = await fetchAuthSession();
                    const authToken = tokens?.accessToken?.toString();
                    const response = await fetch('https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/invite', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': authToken!,
                      },
                      body: JSON.stringify({ email: inviteEmail }),
                    });
                    
                    if (!response.ok) {
                      throw new Error('Failed to send invitation');
                    }
                    
                    setInviteEmail('');
                    setSnackbar({
                      open: true,
                      message: 'Invitation sent successfully!',
                      severity: 'success'
                    });
                  } catch (error) {
                    console.error('Error sending invitation:', error);
                    setSnackbar({
                      open: true,
                      message: 'Failed to send invitation. Please try again.',
                      severity: 'error'
                    });
                  } finally {
                    setIsInviting(false);
                  }
                }}
                sx={{
                  minWidth: { xs: '100%', sm: '200px' },
                  height: '100%',
                  background: 'linear-gradient(45deg, #7B2CBF, #E31B88)',
                  boxShadow: '0 2px 8px rgba(123, 44, 191, 0.25)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #6A24A6, #C41E3A)',
                    boxShadow: '0 4px 12px rgba(123, 44, 191, 0.35)',
                  },
                }}
              >
                Send Invitation
              </Button>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Invite your friends to join TimeCapsule and start sharing memories together!
            </Typography>
          </Box>
        </Container>
      </Box>

      <NotificationsPanel
        open={isNotificationsOpen}
        onClose={handleNotificationClose}
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        isLoading={isLoadingNotifications}
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default FriendsPage;
